import React, { useState, useEffect } from 'react';
import {
  Search,
  Info,
  Calendar,
  Eye,
  UserPlus,
  CheckCircle2,
  AlertTriangle,
  Loader2,
} from 'lucide-react';
import DetailedInfo from './DetailedInfo';
import EndMeeting from './EndMeeting';
import AssignOfficerDialog from './AssignOfficerDialog';
import { diaryAPI, handleAPIError } from './api';
import { useTheme } from '@mui/material/styles';

// ---------------------------------------------------------------------
// Table restyled to match the rest of the app (PerformancePeriods.js,
// Transactions.js, Receipts.js): one bordered card holding the title,
// filter bar, table and footer count, a plain (no gradient) uppercase
// table header, and the same cell padding/size as those tables.
// ---------------------------------------------------------------------

const GroupView = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [searchTerm, setSearchTerm] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  const [showDetailedInfo, setShowDetailedInfo] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState(null);

  const [groupsData, setGroupsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // End Meeting — same dialog DetailedInfo.js's card opens, wired up here
  // too so it doesn't take a trip through Detailed Info just to end one.
  const [endMeetingDialog, setEndMeetingDialog] = useState({
    open: false,
    groupData: null,
    advanceFromOffice: 0
  });
  const [actionFeedback, setActionFeedback] = useState(null); // { type: 'success' | 'error', message }

  // Assign Officer — big dialog with meeting-officer assignment and
  // extended data-access grants.
  const [assignOfficerDialog, setAssignOfficerDialog] = useState({ open: false, group: null });

  const loadGroupsData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [allGroups, allMeetings] = await Promise.all([
        diaryAPI.utils.getGroups(),
        diaryAPI.meetings.getAllMeetingsWithGroups()
      ]);

      const groups = allGroups.results || allGroups || [];

      const meetingsByGroup = {};
      allMeetings.forEach(meeting => {
        const groupId = meeting.group.id;
        if (!meetingsByGroup[groupId]) {
          meetingsByGroup[groupId] = [];
        }
        meetingsByGroup[groupId].push(meeting);
      });

      const groupsWithMeetings = groups.map(group => {
        const groupMeetings = meetingsByGroup[group.id] || [];

        if (groupMeetings.length === 0) {
          return {
            id: group.id,
            group: group.name,
            status: 'Unscheduled',
            nextMeeting: null,
            previousMeeting: null,
            officers: [],
            totalBudget: 'N/A',
            branch: group.branch || 'N/A',
            latestMeetingDate: null,
            sortPriority: 3,
            // End Meeting needs the raw (unformatted) meeting data, kept
            // under separate keys so it doesn't collide with the display
            // fields above — same shape DetailedInfo.js builds for the
            // same dialog.
            hasActiveMeeting: false,
            activeMeetingId: null,
            meetingsRaw: [],
            totalBudgetRaw: 0,
          };
        }

        const sortedMeetings = [...groupMeetings].sort((a, b) =>
          new Date(b.meeting_date || b.previous_meeting_date) -
          new Date(a.meeting_date || a.previous_meeting_date)
        );

        const latestMeeting = sortedMeetings[0];
        const latestMeetingDate = latestMeeting?.meeting_date || latestMeeting?.previous_meeting_date;

        return {
          id: group.id,
          group: group.name,
          status: latestMeeting?.scheduled_status === 'scheduled' ? 'Scheduled' : 'Unscheduled',
          nextMeeting: latestMeeting?.meeting_date || null,
          previousMeeting: latestMeeting?.previous_meeting_date || null,
          officers: latestMeeting?.next_meeting_officers?.map(officer => ({
            name: officer.name,
            avatar: officer.avatar
          })) || [],
          totalBudget: latestMeeting?.total_budget
            ? `KES ${latestMeeting.total_budget.toLocaleString()}`
            : 'N/A',
          branch: group.branch || 'N/A',
          latestMeetingDate: latestMeetingDate,
          sortPriority: latestMeetingDate ? 1 : 3,
          // End Meeting needs the raw (unformatted) meeting data, kept
          // under separate keys so it doesn't collide with the display
          // fields above — same shape DetailedInfo.js builds for the
          // same dialog.
          hasActiveMeeting: true,
          activeMeetingId: latestMeeting?.id || null,
          meetingsRaw: sortedMeetings.map(meeting => ({
            id: meeting.id,
            date: meeting.meeting_date,
            period_start: meeting.period_start,
            approvedLoans: meeting.approved_loans_from_office || 0,
            additionalFunds: meeting.requested_advance_loans || 0,
            meetingStatus: meeting.meeting_status,
            otherExpenses: meeting.total_expenses || 0
          })),
          totalBudgetRaw: latestMeeting?.total_budget || 0,
        };
      });

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const sortedGroups = groupsWithMeetings.sort((a, b) => {
        const dateA = a.latestMeetingDate ? new Date(a.latestMeetingDate) : null;
        const dateB = b.latestMeetingDate ? new Date(b.latestMeetingDate) : null;

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        const dA = new Date(dateA.setHours(0, 0, 0, 0));
        const dB = new Date(dateB.setHours(0, 0, 0, 0));

        const isTodayA = dA.getTime() === today.getTime();
        const isTodayB = dB.getTime() === today.getTime();
        const isFutureA = dA > today;
        const isFutureB = dB > today;

        if (isTodayA && !isTodayB) return -1;
        if (!isTodayA && isTodayB) return 1;

        if (isFutureA && isFutureB) return dA - dB;
        if (isFutureA && !isFutureB) return -1;
        if (!isFutureA && isFutureB) return 1;

        return dB - dA;
      });

      setGroupsData(sortedGroups);
    } catch (err) {
      const errorMessage = handleAPIError(err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroupsData();
  }, []);

  // Auto-dismiss the little success/error toast after a few seconds.
  useEffect(() => {
    if (!actionFeedback) return;
    const timer = setTimeout(() => setActionFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [actionFeedback]);

  const handleDetailedInfo = () => {
    setSelectedGroupId(null);
    setShowDetailedInfo(true);
  };

  const handleBackToGroups = () => {
    setShowDetailedInfo(false);
    setSelectedGroupId(null);
  };

  const handleViewGroup = (groupId) => {
    setSelectedGroupId(groupId);
    setShowDetailedInfo(true);
  };

  const handleAssignOfficer = (group) => {
    setAssignOfficerDialog({ open: true, group });
  };

  const handleAssignOfficerClose = () => {
    setAssignOfficerDialog({ open: false, group: null });
  };

  const handleAssignOfficerUpdated = (message) => {
    // Refresh the table so newly assigned officers / avatars show up.
    loadGroupsData();
    if (message) {
      setActionFeedback({ type: 'success', message });
    }
  };

  // Same End Meeting dialog DetailedInfo.js's card opens — rebuild the
  // "groupData" shape it expects (groupName, meetings, activeMeetingId,
  // totalBudget as a raw number) from this row's raw meeting data.
  const handleEndMeeting = (group) => {
    const groupData = {
      id: group.id,
      groupName: group.group,
      meetings: group.meetingsRaw,
      activeMeetingId: group.activeMeetingId,
      hasActiveMeeting: group.hasActiveMeeting,
      totalBudget: group.totalBudgetRaw,
    };
    const advanceFromOffice = groupData.meetings[0]?.additionalFunds || 0;
    setEndMeetingDialog({ open: true, groupData, advanceFromOffice });
  };

  const handleEndMeetingSubmit = async (endMeetingData) => {
    const { groupData } = endMeetingDialog;
    try {
      await diaryAPI.meetings.endMeeting(groupData.activeMeetingId, endMeetingData);
      await loadGroupsData();
      setEndMeetingDialog({ open: false, groupData: null, advanceFromOffice: 0 });
      setActionFeedback({ type: 'success', message: 'Meeting ended successfully!' });
    } catch (err) {
      const errorMessage = handleAPIError(err);
      setActionFeedback({ type: 'error', message: 'Failed to end meeting: ' + errorMessage });
    }
  };

  // Same gating as the End Meeting button in DetailedInfo.js's card.
  const canEndMeeting = (group) =>
    group.hasActiveMeeting &&
    !!group.meetingsRaw?.[0]?.date &&
    group.meetingsRaw[0]?.meetingStatus === 'ongoing';

  const getStatusBadgeColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'scheduled': return isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
      case 'unscheduled': return isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800';
      default: return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Initials fallback for an officer's circle when no avatar text is given.
  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');
  };

  const filteredGroups = groupsData.filter(group => {
    const matchesSearch = group.group.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBranch = branchFilter === 'all' ||
      (group.branch && group.branch !== 'N/A' && group.branch.toLowerCase() === branchFilter.toLowerCase());
    const matchesStatus = statusFilter === 'all' || group.status.toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesBranch && matchesStatus;
  });

  const availableBranches = [...new Set(groupsData.map(group => group.branch).filter(branch => branch && branch !== 'N/A'))];

  if (showDetailedInfo) {
    return (
      <DetailedInfo
        onBack={handleBackToGroups}
        selectedGroupId={selectedGroupId}
      />
    );
  }

  return (
    <div className="pb-32">
      {/* Loading/error/table are the only things that swap here - the
         dialogs and toast below stay mounted the whole time. A background
         refresh (loadGroupsData() called from inside a dialog after a
         save) sets `loading` briefly too; if that unmounted the dialogs,
         they'd flicker closed and remount right as they were mid-action.
         See: Assign Officer "reappearing" after granting access. */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Loading groups...
          </span>
        </div>
      ) : error ? (
        <div className={`rounded-lg p-6 ${isDark ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'}`}>
          <p className={isDark ? 'text-red-400' : 'text-red-600'}>Failed to load groups: {error}</p>
          <button
            onClick={loadGroupsData}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
          {/* Title */}
          <div className={`flex items-center justify-between gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div>
              <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Groups</h2>
              <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Sorted: Today &rarr; Future &rarr; Past
              </p>
            </div>
            <button
              onClick={handleDetailedInfo}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              <Info className="w-4 h-4" />
              Detailed Info
            </button>
          </div>

          {/* Filters */}
          <div className={`flex flex-wrap items-center gap-3 px-5 py-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="relative flex-1 min-w-[220px]">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <input
                type="text"
                placeholder="Search groups..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? 'bg-[#2b323c] border-gray-700 text-gray-200 placeholder-gray-500 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            {availableBranches.length > 0 && (
              <select
                value={branchFilter}
                onChange={(e) => setBranchFilter(e.target.value)}
                className={`px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value="all">All Branches</option>
                {availableBranches.map(branch => (
                  <option key={branch} value={branch.toLowerCase()}>
                    {branch}
                  </option>
                ))}
              </select>
            )}

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 focus:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="unscheduled">Unscheduled</option>
            </select>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Group
                  </th>
                  <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Next Meeting
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Previous Meeting
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Officer(s)
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Total Budget
                  </th>
                  {availableBranches.length > 0 && (
                    <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      Branch
                    </th>
                  )}
                  <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {filteredGroups.map((group) => (
                  <tr key={group.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                    <td className={`px-6 py-4 font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                      {group.group}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadgeColor(group.status)}`}>
                        {group.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {group.nextMeeting ? (
                        <div className="flex items-center gap-1.5">
                          <Calendar className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                          <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                            {formatDate(group.nextMeeting)}
                          </span>
                        </div>
                      ) : (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase border ${
                          isDark ? 'border-red-800 text-red-400' : 'border-red-300 text-red-700'
                        }`}>
                          Not Scheduled
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5">
                        <Calendar className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                        <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                          {formatDate(group.previousMeeting)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {group.officers.length > 0 ? (
                        <div className="flex items-center">
                          <div className="flex items-center -space-x-2">
                            {group.officers.slice(0, 3).map((officer, idx) => (
                              <div
                                key={idx}
                                title={officer.name}
                                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ring-2 ${
                                  isDark ? 'ring-[#2b323c] bg-blue-900/60 text-blue-200' : 'ring-white bg-blue-100 text-blue-800'
                                }`}
                              >
                                {officer.avatar || getInitials(officer.name)}
                              </div>
                            ))}
                          </div>
                          {group.officers.length > 3 && (
                            <span className={`ml-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              +{group.officers.length - 3} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>N/A</span>
                      )}
                    </td>
                    <td className={`px-6 py-4 font-semibold ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                      {group.totalBudget}
                    </td>
                    {availableBranches.length > 0 && (
                      <td className={`px-6 py-4 font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                        {group.branch}
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewGroup(group.id)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View
                        </button>
                        {/* Assign Officer stacked above End Meeting, aligned
                           vertically as one column. */}
                        <div className="flex flex-col gap-1.5">
                          <button
                            onClick={() => handleAssignOfficer(group)}
                            className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 border text-sm font-medium rounded-lg transition-colors ${
                              isDark ? 'border-gray-600 text-gray-200 hover:border-gray-400' : 'border-gray-300 text-gray-700 hover:border-gray-500'
                            }`}
                          >
                            <UserPlus className="w-3.5 h-3.5" />
                            Assign Officer
                          </button>
                          <button
                            onClick={() => handleEndMeeting(group)}
                            disabled={!canEndMeeting(group)}
                            title={canEndMeeting(group) ? 'End this meeting' : 'Only an ongoing meeting can be ended'}
                            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-600"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            End Meeting
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredGroups.length === 0 && (
            <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
              <p className="text-lg">No groups found</p>
              <p className="text-sm mt-1">Try adjusting your search criteria or filters</p>
            </div>
          )}

          {/* Footer count */}
          <div className={`px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
            Showing {filteredGroups.length} of {groupsData.length} groups (sorted: Today &rarr; Future &rarr; Past)
          </div>
        </div>
      )}

      {/* Assign Officer Dialog — meeting officers + extended data access.
         Stays mounted regardless of the loading/error/table swap above. */}
      <AssignOfficerDialog
        open={assignOfficerDialog.open}
        onClose={handleAssignOfficerClose}
        group={assignOfficerDialog.group}
        onUpdated={handleAssignOfficerUpdated}
      />

      {/* End Meeting Dialog — identical to the one on DetailedInfo.js's card */}
      <EndMeeting
        open={endMeetingDialog.open}
        onClose={() => setEndMeetingDialog({ open: false, groupData: null, advanceFromOffice: 0 })}
        meeting={endMeetingDialog.groupData}
        onEndMeeting={handleEndMeetingSubmit}
        loading={loading}
        advanceFromOffice={endMeetingDialog.advanceFromOffice || 0}
      />

      {/* Success/error toast for End Meeting */}
      {actionFeedback && (
        <div className="fixed bottom-6 right-6 z-[1500]">
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            actionFeedback.type === 'success'
              ? isDark ? 'bg-green-900/90 text-green-200 border border-green-700' : 'bg-green-50 text-green-800 border border-green-200'
              : isDark ? 'bg-red-900/90 text-red-200 border border-red-700' : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {actionFeedback.message}
          </div>
        </div>
      )}
    </div>
  );
};

export default GroupView;